<template>
  <!--
    ProductPicker — a self-contained "pick ONE sellable leaf insurance product"
    control for the new-transaction form. Replaces the old flat dropdown.

    Composition = a trigger button + a CalmModal whose body lifts the
    InsuranceCatalogV2 browse/search composition VERBATIM (SearchPathChips on
    top, responsive BrowseColumns / BrowseDrill below), but with a
    confirm-on-SELLABLE-leaf behaviour.

    MONEY-FLOW SAFETY: a node is confirmable iff its ID is in `sellableIds`
    (the server-derived leaves list). We NEVER use childCountOf===0 as the
    sellable gate — an intermediate whose children are all inactive would
    falsely look like a leaf. Non-sellable taps only NAVIGATE; they never emit
    a selection. v-model emits the chosen leaf NODE (not the id) on `input`.
  -->
  <div class="product-picker calm">
    <!-- ═══════════════ TRIGGER ═══════════════ -->
    <button
      type="button"
      class="product-picker__trigger"
      :class="{ 'is-empty': !selectedNode, 'is-loading': loading }"
      :disabled="loading"
      :aria-haspopup="'dialog'"
      :aria-expanded="String(show)"
      @click="openModal"
    >
      <span class="product-picker__trigger-main">
        <template v-if="selectedNode">
          <span
            v-if="triggerCrumb"
            class="product-picker__trigger-crumb"
            :title="triggerCrumbFull"
          >{{ triggerCrumb }}</span>
          <span class="product-picker__trigger-name" :title="selectedNode.Name">{{
            displayName(selectedNode.Name, true)
          }}</span>
        </template>
        <span v-else class="product-picker__trigger-placeholder">
          {{ $t("lmg.product_picker_placeholder") }}
        </span>
      </span>

      <!-- clear (×) — only when a value is set and not loading. Confirms clear
           by emitting `input(null)` (coordinated with Dev-B). stop so it does
           not also open the modal. -->
      <span
        v-if="selectedNode && !loading"
        class="product-picker__clear"
        role="button"
        tabindex="0"
        :aria-label="$t('lmg.product_picker_clear')"
        :title="$t('lmg.product_picker_clear')"
        @click.stop="onClear"
        @keydown.enter.stop.prevent="onClear"
        @keydown.space.stop.prevent="onClear"
      >&times;</span>

      <span v-else class="product-picker__chevron" aria-hidden="true">▾</span>
    </button>

    <!-- ═══════════════ MODAL ═══════════════ -->
    <calm-modal
      :show.sync="show"
      size="lg"
      :show-footer="false"
      :title="$t('lmg.product_picker_title')"
      @close="onModalClose"
    >
      <div class="product-picker__panel">
        <!-- ───── SEARCH (always on top) ───── -->
        <div class="product-picker__search">
          <el-input
            ref="searchInput"
            v-model="query"
            clearable
            size="small"
            :placeholder="$t('lmg.product_picker_search_placeholder')"
          />
        </div>

        <!-- ───── BODY: loading / search results / responsive browse ───── -->
        <div class="product-picker__body">
          <!-- Loading / empty catalog: never render a broken browse. -->
          <div
            v-if="loading || !roots.length"
            class="product-picker__state"
          >
            {{ loading ? $t("lmg.product_picker_loading") : $t("lmg.product_picker_empty") }}
          </div>

          <!-- SEARCH MODE: a non-empty query → sellable-only path-chip results.
               Results are pre-filtered to sellableIds, so @pick always confirms.
               @pickCrumb navigates into the browse at that ancestor. -->
          <search-path-chips
            v-else-if="query.trim()"
            :results="searchResults"
            :query="query"
            :strip-prefix="true"
            :hide-type-chip="true"
            @pick="onConfirm"
            @pickCrumb="onBrowseTo"
          />

          <!-- BROWSE MODE (query empty): wide = Miller columns, narrow = drill. -->
          <template v-else>
            <browse-columns
              v-if="isWide"
              :roots="roots"
              :childrenOf="catalogChildrenOf"
              :selectionPath="selectionPathIds"
              :selectedId="selectedBrowseId"
              :strip-prefix="true"
              :hide-type-chip="true"
              @select="onBrowseSelect"
            />
            <browse-drill
              v-else
              :breadcrumb="drillBreadcrumb"
              :currentChildren="drillChildren"
              :selectedId="selectedBrowseId"
              :childCountOf="childCountOf"
              :strip-prefix="true"
              :hide-type-chip="true"
              @select="onBrowseSelect"
              @drill="onDrill"
              @up="onUp"
            />
          </template>
        </div>

        <!-- FILTER MODE footer: confirm the highlighted layer (any node). Only
             rendered when allowAnyNode; the sales form never sees this. -->
        <div v-if="allowAnyNode" class="product-picker__confirmbar">
          <span class="product-picker__confirmbar-info">
            <template v-if="highlightedNode">
              เลือก: <strong>{{ displayName(highlightedNode.Name, true) }}</strong>
            </template>
            <template v-else>แตะหมวด/สินค้าในรายการเพื่อเลือก</template>
          </span>
          <button
            type="button"
            class="c-btn c-btn--primary"
            :disabled="!highlightedNode"
            @click="confirmHighlighted"
          >
            ใช้ตัวกรองนี้
          </button>
        </div>
      </div>
    </calm-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Input } from "element-ui";
import CalmModal from "@/components/lmg/CalmModal.vue";
import SearchPathChips from "@/components/lmg/insurance/SearchPathChips.vue";
import BrowseColumns from "@/components/lmg/insurance/BrowseColumns.vue";
import BrowseDrill from "@/components/lmg/insurance/BrowseDrill.vue";
import { displayName } from "@/components/lmg/insurance/insName";

const WIDE_MQ = "(min-width: 1025px)";
const SEARCH_CAP = 50;

export default {
  name: "product-picker",
  components: {
    CalmModal,
    SearchPathChips,
    BrowseColumns,
    BrowseDrill,
    [Input.name]: Input,
  },
  props: {
    // v-model value: the current insuranceID (or null when unset).
    value: { type: [Number, String], default: null },
    // The SET of leaf IDs that are VALID sellable products (server-derived).
    // Accepts Array<Number> or Set — normalised to a Set internally. This is
    // the single source of truth for "confirmable", NOT childCountOf.
    sellableIds: { type: [Array, Set], default: () => [] },
    // true while the catalog is still loading.
    loading: { type: Boolean, default: false },
    // FILTER MODE (default false → ZERO change to the money-flow sales form).
    // When true, ANY catalog node (category / intermediate / leaf) may be
    // confirmed, and row taps only NAVIGATE (never auto-confirm) so the user can
    // drill before confirming the layer via the footer button. Used by the DMS
    // policy-picker filter, where selecting a layer means "all products under it".
    allowAnyNode: { type: Boolean, default: false },
  },
  data() {
    return {
      show: false,
      query: "",
      // in-modal browse state (mirrors InsuranceCatalogV2):
      browseSelectedId: null, // focused/highlighted node in the browse
      drillParentId: null, // narrow-mode: whose children are shown (null=roots)
      isWide: true,
    };
  },
  computed: {
    ...mapGetters("Insure", {
      catalogById: "catalogById",
      catalogChildrenOf: "catalogChildrenOf",
      catalogRoots: "catalogRoots",
      catalogAncestors: "catalogAncestors",
    }),

    roots() {
      return this.catalogRoots || [];
    },

    // Filter mode: the node currently highlighted in the browse (any layer),
    // shown in the footer confirm bar so the user knows what they'll select.
    highlightedNode() {
      if (this.browseSelectedId == null || !this.catalogById) return null;
      return this.catalogById.get(Number(this.browseSelectedId)) || null;
    },

    // Normalise sellableIds (Array | Set) to a Set of Numbers for O(1) gating.
    sellableSet() {
      const src = this.sellableIds;
      const set = new Set();
      if (!src) return set;
      const iter = src instanceof Set ? src : src;
      for (const v of iter) {
        const n = Number(v);
        if (!Number.isNaN(n)) set.add(n);
      }
      return set;
    },

    // ── trigger resolution from `value` ──
    // The selected node, resolved from the v-model value via catalogById. null
    // when unset or not (yet) present in the catalog (e.g. still loading).
    selectedNode() {
      if (this.value == null || !this.catalogById) return null;
      return this.catalogById.get(Number(this.value)) || null;
    },
    // Muted breadcrumb string for the trigger: ancestors root→parent joined by
    // ` › ` (each name stripped of its outline prefix for display).
    triggerCrumb() {
      if (this.value == null) return "";
      const anc = this.ancestorsOf(Number(this.value));
      if (!anc.length) return "";
      return anc.map((a) => displayName(a.Name, true)).join(" › ");
    },
    // Full (unstripped) crumb for the hover title.
    triggerCrumbFull() {
      if (this.value == null) return "";
      return this.ancestorsOf(Number(this.value))
        .map((a) => a.Name)
        .join(" › ");
    },

    // ── browse highlight ──
    // The highlighted node in the browse panes. Seeded from `value` when the
    // modal opens (so the user lands on their current pick), then driven by
    // in-modal navigation.
    selectedBrowseId() {
      return this.browseSelectedId;
    },
    // root → highlighted node (inclusive) — drives the Miller columns unfold.
    selectionPathIds() {
      if (this.browseSelectedId == null) return [];
      return this.ancestorsOf(this.browseSelectedId)
        .map((n) => n.ID)
        .concat(this.browseSelectedId);
    },

    // ── narrow drill state ──
    drillChildren() {
      return this.childrenOf(this.drillParentId == null ? 0 : this.drillParentId);
    },
    drillBreadcrumb() {
      if (this.drillParentId == null) return [];
      const node = this.catalogById
        ? this.catalogById.get(this.drillParentId)
        : null;
      const trail = this.ancestorsOf(this.drillParentId);
      return node ? trail.concat(node) : trail;
    },

    // ── search results (sellable-only) ──
    // Reuses the InsuranceCatalogV2 ranking algo, but ADDS a hard filter: only
    // nodes whose ID is in sellableSet survive. So every result is a confirmable
    // sellable leaf, and @pick can confirm unconditionally.
    searchResults() {
      const q = this.query.trim().toLowerCase();
      if (!q || !this.catalogById) return [];

      const scored = [];
      for (const node of this.catalogById.values()) {
        // SELLABLE GATE — only server-derived sellable leaves can be a result.
        if (!this.sellableSet.has(Number(node.ID))) continue;

        const name = String(node.Name || "");
        const lname = name.toLowerCase();
        const path = this.ancestorsOf(node.ID); // root→parent
        const matchesName = lname.indexOf(q) !== -1;
        const matchesPath =
          !matchesName &&
          path.some(
            (p) => String(p.Name || "").toLowerCase().indexOf(q) !== -1
          );
        if (!matchesName && !matchesPath) continue;

        // Rank: exact (0) > prefix (1) > name substring (2) > path-only (3).
        let rank;
        if (lname === q) rank = 0;
        else if (lname.indexOf(q) === 0) rank = 1;
        else if (matchesName) rank = 2;
        else rank = 3;

        scored.push({ node, pathNodes: path, rank });
      }

      scored.sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        return String(a.node.Name || "").localeCompare(
          String(b.node.Name || ""),
          "th"
        );
      });

      // Every result is a sellable leaf → isLeaf:true.
      return scored.slice(0, SEARCH_CAP).map(({ node, pathNodes }) => ({
        node,
        pathNodes,
        isLeaf: true,
      }));
    },
  },
  watch: {
    show(open) {
      if (open) {
        // Seed the browse to the current value so the user lands on their pick.
        this.seedBrowseFromValue();
        this.query = "";
        this.$nextTick(this.focusSearch);
      }
    },
  },
  methods: {
    displayName,

    // ── store helpers (mirror InsuranceCatalogV2) ──
    childrenOf(id) {
      if (!this.catalogChildrenOf) return [];
      return this.catalogChildrenOf.get(id) || [];
    },
    childCountOf(id) {
      if (id == null) return 0;
      return (
        (this.catalogChildrenOf && this.catalogChildrenOf.get(id)) || []
      ).length;
    },
    ancestorsOf(id) {
      if (id == null || typeof this.catalogAncestors !== "function") return [];
      return this.catalogAncestors(id) || [];
    },
    isSellable(node) {
      return !!node && this.sellableSet.has(Number(node.ID));
    },

    // ── trigger / modal open-close ──
    openModal() {
      if (this.loading) return;
      this.show = true;
    },
    onModalClose() {
      // Return focus to the trigger for a11y / keyboard users.
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector(".product-picker__trigger");
        if (el && typeof el.focus === "function") el.focus();
      });
    },
    onClear() {
      // Coordinated with Dev-B: clearing emits input(null).
      this.$emit("input", null);
    },
    focusSearch() {
      const cmp = this.$refs.searchInput;
      if (cmp && typeof cmp.focus === "function") cmp.focus();
    },
    seedBrowseFromValue() {
      if (this.value != null && this.catalogById && this.catalogById.get(Number(this.value))) {
        const id = Number(this.value);
        const node = this.catalogById.get(id);
        this.browseSelectedId = id;
        this.drillParentId = node.UpperID === 0 ? null : node.UpperID;
      } else {
        this.browseSelectedId = null;
        this.drillParentId = null;
      }
    },

    // ── CONFIRM (money-flow critical) ──
    // Only a SELLABLE node may be confirmed. A non-sellable node (category,
    // intermediate, or a pseudo-leaf with no active children) is treated as a
    // navigation target — never a selection.
    onConfirm(node) {
      if (!node) return;
      // allowAnyNode (filter mode) lets a category/intermediate confirm too;
      // default path is unchanged — only a SELLABLE leaf may confirm.
      if (this.allowAnyNode || this.isSellable(node)) {
        this.$emit("input", node); // emit the NODE (v-model value+input pair)
        this.show = false;
      } else {
        // Not sellable → navigate into it instead of confirming.
        this.navigateInto(node);
      }
    },

    // ── browse interactions ──
    // A row tap in either browse pane: confirm a sellable leaf, else navigate.
    onBrowseSelect(node) {
      if (!node) return;
      // Filter mode: a tap only NAVIGATES/highlights — confirmation is an
      // explicit footer action so the user can drill into any layer first.
      if (this.allowAnyNode) {
        this.navigateInto(node);
        return;
      }
      if (this.isSellable(node)) {
        this.onConfirm(node);
      } else {
        this.navigateInto(node);
      }
    },

    // Filter mode footer: confirm the currently highlighted node (any layer).
    confirmHighlighted() {
      if (this.browseSelectedId == null || !this.catalogById) return;
      const node = this.catalogById.get(Number(this.browseSelectedId));
      if (node) this.onConfirm(node);
    },
    // Move the highlight / unfold the path to a node WITHOUT confirming. In wide
    // mode the selectionPath computed makes the Miller columns expand; in narrow
    // mode we drill into the node so its children show.
    navigateInto(node) {
      this.browseSelectedId = node.ID;
      if (this.isWide) {
        // wide: keep drill in sync for a later viewport rotation.
        this.drillParentId = node.UpperID === 0 ? null : node.UpperID;
      } else {
        // narrow: descend into this node (show its children).
        this.drillParentId = node.ID;
      }
    },
    onDrill(node) {
      if (!node) return;
      this.drillParentId = node.ID;
      this.browseSelectedId = node.ID;
    },
    onUp(node) {
      this.drillParentId = node ? node.ID : null;
      this.browseSelectedId = node ? node.ID : null;
    },

    // ── search crumb → jump into browse ──
    // Picking an ancestor chip in a search result navigates to that ancestor in
    // the browse (clearing the query so the browse is shown). It NEVER confirms
    // (ancestors are not sellable leaves).
    onBrowseTo(node) {
      if (!node) return;
      this.browseSelectedId = node.ID;
      this.drillParentId = node.UpperID === 0 ? null : node.UpperID;
      this.query = "";
    },

    // ── responsive ──
    applyMql(e) {
      this.isWide = e.matches;
    },
  },
  mounted() {
    if (typeof window !== "undefined" && window.matchMedia) {
      this._mql = window.matchMedia(WIDE_MQ);
      this.isWide = this._mql.matches;
      this._mqlHandler = (e) => this.applyMql(e);
      if (this._mql.addEventListener) {
        this._mql.addEventListener("change", this._mqlHandler);
      } else if (this._mql.addListener) {
        this._mql.addListener(this._mqlHandler);
      }
    }
  },
  beforeDestroy() {
    if (this._mql && this._mqlHandler) {
      if (this._mql.removeEventListener) {
        this._mql.removeEventListener("change", this._mqlHandler);
      } else if (this._mql.removeListener) {
        this._mql.removeListener(this._mqlHandler);
      }
    }
    this._mql = null;
    this._mqlHandler = null;
  },
};
</script>

<style scoped>
.product-picker {
  width: 100%;
  /*
   * The root carries the `calm` class ONLY to resolve the --c-* design tokens
   * for the trigger + the modal slots. But `.calm` (calm.scss) also sets
   * `min-height: 100vh` and a page `background`, which would force this inline
   * control to fill the whole viewport — that 100vh then becomes the grid row
   * height and balloons the trigger. Neutralise those page-level side-effects
   * so the picker sizes to its content like any normal field control.
   */
  min-height: 0;
  background: transparent;
  /*
   * `.field-grid` has no `align-items`, so its grid items default to
   * `align-items: stretch`. Anchor the picker to the top of its grid cell so a
   * taller sibling in the same row can never stretch it.
   */
  align-self: start;
  display: flex;
  flex-direction: column;
}

/* ═══════════════ TRIGGER ═══════════════ */
.product-picker__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  /*
   * Pin the control to a normal field height (matches the adjacent
   * .ed-input el-select, whose inner is 40px + border). A fixed height means
   * the trigger can never balloon even if an ancestor is stretched. The two
   * trigger lines (crumb + name) fit comfortably within 44px.
   */
  height: 44px;
  min-height: 44px; /* iPad touch target */
  align-self: flex-start; /* never stretch within the flex column */
  flex: none;
  box-sizing: border-box;
  padding: 6px 12px;
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 10px;
  background: var(--c-surface, #fff);
  color: var(--c-text, #2c3e50);
  font-family: var(--c-font, inherit);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: border-color var(--c-fast, 140ms) var(--c-ease, ease),
    background-color var(--c-fast, 140ms) var(--c-ease, ease);
}
.product-picker__trigger:hover:not(:disabled) {
  border-color: var(--c-blue, #3b6ea8);
  background: var(--c-blue-soft, #ebf2fa);
}
.product-picker__trigger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--c-blue, #3b6ea8);
}
.product-picker__trigger:disabled {
  opacity: 0.6;
  cursor: default;
}

.product-picker__trigger-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.product-picker__trigger-crumb {
  font-size: 11px;
  line-height: 1.2;
  color: var(--c-text-mute, #8a96a8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.product-picker__trigger-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--c-text, #2c3e50);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.product-picker__trigger-placeholder {
  color: var(--c-text-mute, #8a96a8);
}

.product-picker__clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: var(--c-text-mute, #8a96a8);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: background-color var(--c-fast, 140ms) var(--c-ease, ease),
    color var(--c-fast, 140ms) var(--c-ease, ease);
}
.product-picker__clear:hover {
  background: var(--c-neutral-soft, #eef0f4);
  color: var(--c-text, #2c3e50);
}
.product-picker__clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--c-blue, #3b6ea8);
}

.product-picker__chevron {
  flex: 0 0 auto;
  color: var(--c-text-mute, #8a96a8);
  font-size: 12px;
}

/* ═══════════════ MODAL PANEL ═══════════════ */
.product-picker__panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* the modal body scrolls; cap the browse area so columns/drill scroll inside */
  height: min(70vh, 560px);
}
.product-picker__search {
  flex: 0 0 auto;
  margin-bottom: 12px;
}
.product-picker__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden; /* inner panes own their scroll */
  display: flex;
  flex-direction: column;
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 10px;
  background: var(--c-surface, #fff);
}
/* filter-mode confirm bar (allowAnyNode only) */
.product-picker__confirmbar {
  flex: 0 0 auto;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 10px;
  background: var(--c-surface-soft, #f7f9fc);
}
.product-picker__confirmbar-info {
  font-size: 14px;
  color: var(--c-text-mute, #7b8794);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-picker__confirmbar-info strong {
  color: var(--c-text, #1d2733);
}
/* let the search-results list scroll within the body */
.product-picker__body >>> .spc {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
}

.product-picker__state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  padding: 24px 8px;
  color: var(--c-text-mute, #8a96a8);
  font-size: 14px;
  text-align: center;
}
</style>
